import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import {
    getActivateDeactivateUpdateStatus, getUserAccessHistory,
    userActiveDeactiveReset
} from "../../app/store/users";
import { getAlert, getConfirmAlert } from "../../Services/appAlerts";
import { getCurrentUser } from "../../app/store/auth";
import {
    useGetUserAccessLogHistoryQuery,
    useUpdateUserActiveDeactiveMutation
} from "../../app/store/usersApiSlice";

const HISTORY_STYLES = {
    modalDialog: {
        maxWidth: "920px"
    },
    modalContent: {
        borderRadius: "26px",
        overflow: "hidden",
        border: "1px solid rgba(226, 232, 240, 1)",
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)"
    },
    modalHeader: {
        padding: "18px 22px",
        borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
        background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
    },
    headerContent: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "14px"
    },
    titleWrap: {
        display: "flex",
        alignItems: "center",
        gap: "14px"
    },
    headerIcon: {
        width: "46px",
        height: "46px",
        borderRadius: "15px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(79, 70, 229, 0.16) 100%)",
        color: "#4338ca",
        fontSize: "21px",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)"
    },
    title: {
        fontSize: "20px",
        fontWeight: 800,
        color: "#0f172a",
        lineHeight: 1.1,
        margin: 0
    },
    subtitle: {
        fontSize: "12px",
        color: "#64748b",
        marginTop: "4px"
    },
    closeButton: {
        width: "38px",
        height: "38px",
        borderRadius: "999px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "#ffffff",
        color: "#64748b",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "16px",
        fontWeight: 800,
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
        flexShrink: 0
    },
    modalBody: {
        padding: "20px 22px",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
    },
    modalFooter: {
        padding: "16px 22px 20px 22px",
        borderTop: "1px solid rgba(226, 232, 240, 0.9)",
        background: "#ffffff"
    },
    footerActions: {
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px"
    },
    cancelButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "999px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "#ffffff",
        color: "#475569",
        padding: "10px 24px",
        fontSize: "12px",
        fontWeight: 800
    },
    saveButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "999px",
        border: "none",
        background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
        color: "#ffffff",
        padding: "10px 24px",
        fontSize: "12px",
        fontWeight: 800,
        boxShadow: "0 10px 15px -3px rgba(37, 99, 235, 0.3)"
    },
    formCard: {
        borderRadius: "20px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "#ffffff",
        boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
        padding: "20px"
    },
    targetCard: {
        borderRadius: "18px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
        padding: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "14px",
        marginBottom: "18px"
    },
    targetInfo: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minWidth: 0
    },
    avatar: {
        width: "44px",
        height: "44px",
        borderRadius: "14px",
        background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
        color: "#ffffff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "15px",
        fontWeight: 800,
        flexShrink: 0
    },
    targetName: {
        fontSize: "15px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "2px",
        wordBreak: "break-word"
    },
    targetMeta: {
        fontSize: "12px",
        fontWeight: 700,
        color: "#64748b"
    },
    optionGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "12px",
        marginBottom: "18px"
    },
    optionCard: {
        borderRadius: "18px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "#ffffff",
        padding: "16px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        minHeight: "82px",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
        transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease"
    },
    optionCardActive: {
        border: "1px solid rgba(37, 99, 235, 0.55)",
        background: "linear-gradient(135deg, rgba(239, 246, 255, 0.92) 0%, rgba(238, 242, 255, 0.9) 100%)",
        boxShadow: "0 14px 28px rgba(37, 99, 235, 0.12)"
    },
    optionIcon: {
        width: "42px",
        height: "42px",
        borderRadius: "14px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "16px"
    },
    optionTitle: {
        display: "block",
        fontSize: "14px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "3px"
    },
    optionText: {
        display: "block",
        fontSize: "12px",
        fontWeight: 600,
        color: "#64748b",
        lineHeight: 1.35
    },
    textarea: {
        minHeight: "112px",
        borderRadius: "16px",
        border: "1px solid rgba(203, 213, 225, 1)",
        resize: "vertical",
        padding: "12px 14px",
        fontSize: "14px",
        color: "#0f172a",
        boxShadow: "none"
    },
    statsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "12px",
        marginBottom: "18px"
    },
    statCard: {
        borderRadius: "18px",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: "#ffffff",
        padding: "14px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)"
    },
    statIcon: {
        width: "40px",
        height: "40px",
        borderRadius: "14px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "15px"
    },
    statLabel: {
        fontSize: "11px",
        fontWeight: 800,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.08em"
    },
    statValue: {
        fontSize: "22px",
        fontWeight: 900,
        color: "#0f172a",
        lineHeight: 1.1
    },
    tableWrap: {
        borderRadius: "18px",
        border: "1px solid rgba(226, 232, 240, 1)",
        overflow: "hidden",
        background: "#ffffff"
    },
    table: {
        marginBottom: 0,
        color: "#334155"
    },
    th: {
        border: "none",
        borderBottom: "1px solid rgba(226, 232, 240, 1)",
        background: "#f8fafc",
        color: "#64748b",
        fontSize: "11px",
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        padding: "13px 14px",
        whiteSpace: "nowrap"
    },
    td: {
        border: "none",
        borderBottom: "1px solid rgba(241, 245, 249, 1)",
        padding: "14px",
        verticalAlign: "middle",
        fontSize: "13px",
        fontWeight: 600
    },
    statusPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        borderRadius: "999px",
        padding: "7px 11px",
        fontSize: "12px",
        fontWeight: 800,
        whiteSpace: "nowrap"
    },
    emptyState: {
        borderRadius: "18px",
        border: "1px dashed rgba(148, 163, 184, 0.7)",
        background: "#ffffff",
        padding: "34px 18px",
        textAlign: "center",
        color: "#64748b",
        fontSize: "14px",
        fontWeight: 700
    },
    label: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: "8px"
    }
};

const ActiveDeactiveHistory = (props) => {
    const { logShowHide, handleLogShowHide, actionType, selectedObj, type, pageType } = props;
    const [status, setStatus] = useState(null);
    const [notes, setNotes] = useState("");

    const { data: userAccessHistory } = useGetUserAccessLogHistoryQuery(
        { id: selectedObj?.id, type },
        { skip: !logShowHide || actionType !== "log" || !selectedObj?.id }
    );
    const [updateUserActiveDeactive, { isLoading: isSaving }] = useUpdateUserActiveDeactiveMutation();

    const userAccessHistorySelector = useSelector(getUserAccessHistory);
    const activateDeactivateUpdateStatusSelector = useSelector(getActivateDeactivateUpdateStatus);
    const dispatch = useDispatch();
    const currentUser = getCurrentUser();
    const isActionMode = actionType === "action";
    const subjectName = `${selectedObj?.name || ""} ${selectedObj?.lastName || ""}`.trim() || selectedObj?.performerName || pageType || "User";
    const subjectMeta = selectedObj?.email || selectedObj?.title || `${pageType || "User"} access`;
    const subjectInitials = subjectName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((item) => item.charAt(0))
        .join("")
        .toUpperCase() || "U";

    const accessHistory = useMemo(() => {
        return userAccessHistory || userAccessHistorySelector || [];
    }, [userAccessHistory, userAccessHistorySelector]);

    const activatedCount = useMemo(() => accessHistory.filter((item) => item.status).length, [accessHistory]);
    const deactivatedCount = useMemo(() => accessHistory.filter((item) => !item.status).length, [accessHistory]);
    const selectedStatusLabel = status ? "Active" : "Inactive";

    useEffect(() => {
        if (selectedObj) {
            setStatus(selectedObj.status);
            setNotes("");
        } else {
            setNotes("");
        }
    }, [selectedObj]);

    const currentUserId = currentUser?.id;
    const selectedObjId = selectedObj?.id;
    const selectedObjPartnerId = selectedObj?.partnerId;

    const handleSubmit = useCallback(async () => {
        let title = status ? "Activate" : "Deactivate";
        const result = await getConfirmAlert("Warning", `Are you sure you want to ${title} the ${pageType}?`, "warning");
        if (result.isConfirmed) {
            try {
                await updateUserActiveDeactive({
                    addedBy: currentUserId,
                    status: status,
                    notes: notes,
                    performerId: selectedObjId,
                    partnerId: selectedObjPartnerId
                }).unwrap();
            } catch (err) {
                // error is handled or ignored
            }
        }
    }, [status, pageType, updateUserActiveDeactive, currentUserId, notes, selectedObjId, selectedObjPartnerId]);

    const getStatusPillStyle = useCallback((value) => ({
        ...HISTORY_STYLES.statusPill,
        color: value ? "#166534" : "#991b1b",
        background: value ? "rgba(220, 252, 231, 0.9)" : "rgba(254, 226, 226, 0.9)",
        border: value ? "1px solid rgba(187, 247, 208, 1)" : "1px solid rgba(254, 202, 202, 1)"
    }), []);

    useEffect(() => {
        if (activateDeactivateUpdateStatusSelector) {
            let title = status ? "Activate" : "Deactivate";
            getAlert("", `${title}d Successfully`, "success");
            handleLogShowHide("save");
            dispatch(userActiveDeactiveReset())
        }
    }, [activateDeactivateUpdateStatusSelector, status, handleLogShowHide, dispatch]);

    return (
        <Modal show={logShowHide} size="lg" centered dialogClassName="active-history-modal">
            <style>
                {`
                    .active-history-modal {
                        max-width: ${HISTORY_STYLES.modalDialog.maxWidth};
                    }
                    .active-history-modal .modal-content {
                        border-radius: ${HISTORY_STYLES.modalContent.borderRadius};
                        overflow: hidden;
                        border: ${HISTORY_STYLES.modalContent.border};
                        box-shadow: ${HISTORY_STYLES.modalContent.boxShadow};
                    }
                    .active-history-modal .btn:focus,
                    .active-history-modal .btn:active,
                    .active-history-modal textarea:focus,
                    .active-history-modal input:focus {
                        outline: none !important;
                        box-shadow: none !important;
                    }
                    .active-history-modal textarea:focus {
                        border-color: rgba(37, 99, 235, 0.55) !important;
                    }
                    .active-history-table tbody tr:hover td {
                        background: #f8fbff;
                    }
                    @media (max-width: 575.98px) {
                        .active-history-footer {
                            flex-direction: column-reverse;
                        }
                        .active-history-footer .btn {
                            width: 100%;
                            justify-content: center;
                        }
                    }
                `}
            </style>
            <Modal.Header style={HISTORY_STYLES.modalHeader}>
                <div style={HISTORY_STYLES.headerContent}>
                    <div style={HISTORY_STYLES.titleWrap}>
                        <div style={HISTORY_STYLES.headerIcon}>
                            <i className={`fa ${isActionMode ? "fa-shield" : "fa-history"}`} aria-hidden="true"></i>
                        </div>
                        <div>
                            <Modal.Title style={HISTORY_STYLES.title}>
                                {isActionMode ? "Activate/Deactivate Status" : "Access Status History"}
                            </Modal.Title>
                            <div style={HISTORY_STYLES.subtitle}>
                                {isActionMode ? `Update access status for ${subjectName}.` : `Review activation activity for ${subjectName}.`}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        style={HISTORY_STYLES.closeButton}
                        onClick={() => handleLogShowHide("", "")}
                        aria-label="Close"
                    >
                        <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body style={HISTORY_STYLES.modalBody}>
                <div style={HISTORY_STYLES.formCard}>
                    <div style={HISTORY_STYLES.targetCard}>
                        <div style={HISTORY_STYLES.targetInfo}>
                            <div style={HISTORY_STYLES.avatar}>{subjectInitials}</div>
                            <div>
                                <div style={HISTORY_STYLES.targetName}>{subjectName}</div>
                                <div style={HISTORY_STYLES.targetMeta}>{subjectMeta}</div>
                            </div>
                        </div>
                        <span style={getStatusPillStyle(status)}>
                            <i className={`fa ${status ? "fa-check" : "fa-times"}`} aria-hidden="true"></i>
                            {selectedStatusLabel}
                        </span>
                    </div>
                    <div>
                        {isActionMode && (
                            <React.Fragment>
                                <div style={HISTORY_STYLES.optionGrid}>
                                    <label
                                        htmlFor="activate"
                                        style={{
                                            ...HISTORY_STYLES.optionCard,
                                            ...(status === true ? HISTORY_STYLES.optionCardActive : {})
                                        }}
                                    >
                                        <input
                                            name="status"
                                            id="activate"
                                            type="radio"
                                            checked={status === true}
                                            onChange={() => setStatus(true)}
                                            style={{ margin: 0 }}
                                        />
                                        <span style={{ ...HISTORY_STYLES.optionIcon, background: "rgba(220, 252, 231, 0.9)", color: "#166534" }}>
                                            <i className="fa fa-check" aria-hidden="true"></i>
                                        </span>
                                        <span>
                                            <span style={HISTORY_STYLES.optionTitle}>Activate</span>
                                            <span style={HISTORY_STYLES.optionText}>Allow this user to access the portal.</span>
                                        </span>
                                    </label>
                                    <label
                                        htmlFor="deactivate"
                                        style={{
                                            ...HISTORY_STYLES.optionCard,
                                            ...(status === false ? HISTORY_STYLES.optionCardActive : {})
                                        }}
                                    >
                                        <input
                                            name="status"
                                            id="deactivate"
                                            type="radio"
                                            checked={status === false}
                                            onChange={() => setStatus(false)}
                                            style={{ margin: 0 }}
                                        />
                                        <span style={{ ...HISTORY_STYLES.optionIcon, background: "rgba(254, 226, 226, 0.9)", color: "#991b1b" }}>
                                            <i className="fa fa-ban" aria-hidden="true"></i>
                                        </span>
                                        <span>
                                            <span style={HISTORY_STYLES.optionTitle}>Deactivate</span>
                                            <span style={HISTORY_STYLES.optionText}>Block access while preserving history.</span>
                                        </span>
                                    </label>
                                </div>
                                <div>
                                    <label htmlFor="notes" style={HISTORY_STYLES.label}>Notes</label>
                                    <textarea
                                        name="notes"
                                        id="notes"
                                        cols="2"
                                        rows="4"
                                        value={notes}
                                        className="form-control history-notes"
                                        style={HISTORY_STYLES.textarea}
                                        placeholder="Add context for this status change"
                                        onChange={({ currentTarget }) => setNotes(currentTarget.value)}
                                    ></textarea>
                                </div>
                            </React.Fragment>
                        )}
                        {!isActionMode && (
                            <React.Fragment>
                                <div style={HISTORY_STYLES.statsGrid}>
                                    <div style={HISTORY_STYLES.statCard}>
                                        <span style={{ ...HISTORY_STYLES.statIcon, background: "rgba(219, 234, 254, 0.9)", color: "#1d4ed8" }}>
                                            <i className="fa fa-list" aria-hidden="true"></i>
                                        </span>
                                        <div>
                                            <div style={HISTORY_STYLES.statLabel}>Total Logs</div>
                                            <div style={HISTORY_STYLES.statValue}>{accessHistory.length}</div>
                                        </div>
                                    </div>
                                    <div style={HISTORY_STYLES.statCard}>
                                        <span style={{ ...HISTORY_STYLES.statIcon, background: "rgba(220, 252, 231, 0.9)", color: "#166534" }}>
                                            <i className="fa fa-check" aria-hidden="true"></i>
                                        </span>
                                        <div>
                                            <div style={HISTORY_STYLES.statLabel}>Activated</div>
                                            <div style={HISTORY_STYLES.statValue}>{activatedCount}</div>
                                        </div>
                                    </div>
                                    <div style={HISTORY_STYLES.statCard}>
                                        <span style={{ ...HISTORY_STYLES.statIcon, background: "rgba(254, 226, 226, 0.9)", color: "#991b1b" }}>
                                            <i className="fa fa-ban" aria-hidden="true"></i>
                                        </span>
                                        <div>
                                            <div style={HISTORY_STYLES.statLabel}>Deactivated</div>
                                            <div style={HISTORY_STYLES.statValue}>{deactivatedCount}</div>
                                        </div>
                                    </div>
                                </div>
                                {accessHistory.length > 0 ? (
                                    <div className="table-responsive" style={HISTORY_STYLES.tableWrap}>
                                        <table className="table active-history-table" style={HISTORY_STYLES.table}>
                                            <thead>
                                            <tr>
                                                <th style={HISTORY_STYLES.th}>#</th>
                                                <th style={HISTORY_STYLES.th}>Date & Time</th>
                                                <th style={HISTORY_STYLES.th}>Notes</th>
                                                <th style={HISTORY_STYLES.th}>Action By</th>
                                                <th style={HISTORY_STYLES.th}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accessHistory.map((item, key) => (
                                                <tr key={key}>
                                                    <td style={HISTORY_STYLES.td}>{key + 1}</td>
                                                    <td style={HISTORY_STYLES.td}>
                                                        <Moment format="MMM DD YYYY, h:mm a">{item.addedDateTime}</Moment>
                                                    </td>
                                                    <td style={HISTORY_STYLES.td}>{item.notes || "No notes added"}</td>
                                                    <td style={HISTORY_STYLES.td}>{item.performerName || "-"}</td>
                                                    <td style={HISTORY_STYLES.td}>
                                                        <span style={getStatusPillStyle(item.status)}>
                                                            <i className={`fa ${item.status ? "fa-check" : "fa-times"}`} aria-hidden="true"></i>
                                                            {item.status ? "Activated" : "Deactivated"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                ) : (
                                    <div style={HISTORY_STYLES.emptyState}>
                                        <i className="fa fa-history d-block mb-2" aria-hidden="true"></i>
                                        No activation history found.
                                    </div>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer style={HISTORY_STYLES.modalFooter}>
                <div className="active-history-footer" style={HISTORY_STYLES.footerActions}>
                    <button
                        className="btn"
                        style={HISTORY_STYLES.cancelButton}
                        onClick={() => handleLogShowHide("", "")}
                    >
                        <i className="fa fa-times" aria-hidden="true"></i>
                        {isActionMode ? "Cancel" : "Close"}
                    </button>
                    {isActionMode && (
                        <button className="btn" style={HISTORY_STYLES.saveButton} onClick={handleSubmit} disabled={isSaving || status === null}>
                            {isSaving ? (
                                <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i> {status ? "Activating..." : "Deactivating..."}</>
                            ) : (
                                <><i className="fa fa-check mr-1" aria-hidden="true"></i> {status ? "Activate" : "Deactivate"}</>
                            )}
                        </button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
}


export default ActiveDeactiveHistory;
